import { ClockIcon } from '@heroicons/react/20/solid';
import { twMerge } from 'tailwind-merge';

import Skeleton from '@/components/UI/Skeleton';
import { ArrowTrendingDownIcon, CurrencyDollarIcon } from '@heroicons/react/16/solid';

export const PriceInfo: React.FC<{
  price: number;
  originalPrice: number;
  earlyBird?: number;
  earlyBirdEndDate?: string;
  locale?: string;
  variety?: API.MembershipProductOffer;
  stats?: API.MembershipProductStats;
  discount?: number;
  balance?: number;
  loading?: boolean;
}> = ({
  price,
  earlyBird,
  locale,
  originalPrice,
  variety,
  // stats,
  discount = 0,
  balance = 0,
  earlyBirdEndDate,
  loading = false,
}) => {
  const displayPrice = originalPrice + (variety?.addOn?.price || 0);
  let balanceUsed = Math.min(balance, price);
  let priceSuffix = '';
  const isAnnual = variety?.time?.subscription?.interval === '1 years';
  if (variety?.time?.subscription?.interval) {
    priceSuffix = '/ month';
    balanceUsed = 0; // cannot use balance on Subscriptions
    // @todo https://github.com/parkerrome/raspicks/issues/897
  }

  return (
    <div className="mb-4">
      {!!earlyBird && (
        <div className="mb-3 flex flex-col items-start space-y-2">
          <p className="inline-flex w-fit items-center space-x-2 rounded-sm bg-red-500 px-2 py-1 font-semibold text-white text-xs tracking-wide">
            <ClockIcon height={20} className="size-5 shrink-0" />
            <span>Early bird special</span>
          </p>
          <div className="text-xs text-zinc-700 dark:text-zinc-200">
            <span>
              {new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0,
              }).format(earlyBird)}{' '}
              off
            </span>
            {earlyBirdEndDate && <span> ends {earlyBirdEndDate}</span>}
          </div>
        </div>
      )}
      <div className="flex items-baseline">
        {!!earlyBird && (
          <p
            className={twMerge(
              'font-semibold text-3xl text-zinc-900 tracking-tight dark:text-zinc-100',
              (!!discount || !!balance) &&
                'font-normal text-xl text-zinc-500 line-through dark:text-zinc-300',
            )}
          >
            <span>
              {new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0,
              }).format(displayPrice)}
            </span>
            {priceSuffix && (
              <span className="ml-1 font-normal text-semibold text-sm text-zinc-600 leading-6 tracking-normal dark:text-zinc-400">
                {priceSuffix}
              </span>
            )}
          </p>
        )}
        <p
          className={twMerge(
            'font-semibold text-3xl text-zinc-900 tracking-tight dark:text-zinc-100',
            earlyBird && 'text-lg! text-red-500! ms-2 line-through',
            (!!discount || !!balance) && 'font-normal text-xl text-zinc-500 dark:text-zinc-300',
          )}
        >
          <Skeleton loading={loading}>
            <span className={twMerge((!!discount || !!balance) && 'line-through')}>
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: isAnnual ? 2 : 0,
              }).format(
                isAnnual ? (displayPrice + (earlyBird || 0)) / 12 : displayPrice + (earlyBird || 0),
              )}
            </span>
          </Skeleton>
          {priceSuffix && (
            <span className="ml-1 font-normal text-semibold text-sm text-zinc-600 leading-6 tracking-normal dark:text-zinc-400">
              {priceSuffix}
            </span>
          )}
        </p>
      </div>
      {(!!discount || !!balance) && (
        <div className="mt-4 text-emerald-700 dark:text-emerald-100">
          <p className="flex flex-row space-x-1 font-medium text-sm">
            <ArrowTrendingDownIcon height={16} className="size-4" />
            <span>Your price {!!discount && `(${discount * 100}% off)`}</span>
          </p>
          <p className="flex flex-row items-baseline space-x-2 font-semibold text-3xl tracking-tight">
            <span>
              {new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: isAnnual ? 2 : 0,
              }).format(isAnnual ? (price - balanceUsed) / 12 : price - balanceUsed)}
            </span>
            {priceSuffix && (
              <span className="ml-1 font-normal text-semibold text-sm text-zinc-600 leading-6 tracking-normal dark:text-zinc-400">
                {priceSuffix}
              </span>
            )}
          </p>
          {!!balance && !!balanceUsed && (
            <div className="mt-2 flex flex-row items-center space-x-1 font-medium text-xs">
              <CurrencyDollarIcon height={16} className="size-4 shrink-0" />
              <span>
                With{' '}
                {new Intl.NumberFormat(locale, {
                  style: 'currency',
                  currency: 'USD',
                  maximumFractionDigits: 0,
                }).format(balanceUsed)}{' '}
                of your{' '}
                {new Intl.NumberFormat(locale, {
                  style: 'currency',
                  currency: 'USD',
                  maximumFractionDigits: 0,
                }).format(balance)}{' '}
                balance applied
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
